import {
  getGetApiGroupQueryOptions,
  useGetApiInstitution,
  type GroupViewDTO,
} from '@smartsash/api-client';
import type { TableFilterFieldOverrides } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import type { TableMeta } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GROUPS_CITY_FIELD,
  GROUPS_INSTITUTION_FIELD,
  groupColumnText,
  groupsQueryParams,
  type GroupColumnId,
  type GroupsSearch,
} from './groupsModel';

import {
  LOOKUP_PAGE_SIZE,
  useInstitutionNames,
  useLabCountByGroupId,
} from '@/hooks/useLookupNames';
import { DataScreenFilter, filterColumnsFromText } from '@/ui/shared/TableFilter';

/**
 * The filter builder for the Groups list (HS-869 + the filtering-foundation follow-up).
 *
 * The rows come from the **same** query key the screen renders — `groupsQueryParams` builds it
 * identically here and in `groupsScreen` — so this is a cache read rather than a second request,
 * and the fields are derived from the same column-text map the table uses. The institution and
 * laboratories readers need the joined lookups, so they are bound to this render's `meta`. `type`
 * has no entry: it is a design-only placeholder with no backing field on `GroupViewDTO` (see
 * `groupsModel`), so it is deliberately absent from the filter vocabulary.
 *
 * Institution's options are overridden to the full institution list rather than derived from the
 * loaded page — pagination is server-driven, so the ten loaded rows would offer only the
 * institutions among them — and the field carries ids rather than names, since `institutionId` is
 * what `GetApiGroupParams` narrows on. City has no vocabulary endpoint, so it is typed rather than
 * picked, and it is not a column at all, so it is appended as an extra field.
 */
export function GroupsFilter({ search }: { search: GroupsSearch }) {
  const { t } = useTranslation();
  const { data } = useQuery(getGetApiGroupQueryOptions(groupsQueryParams(search)));
  const institutionNames = useInstitutionNames();
  const labCountByGroupId = useLabCountByGroupId();
  const { data: institutionData } = useGetApiInstitution({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  const meta = useMemo<TableMeta<GroupViewDTO>>(
    () => ({ institutionNames, labCountByGroupId }),
    [institutionNames, labCountByGroupId],
  );

  const columns = useMemo(
    () =>
      filterColumnsFromText<GroupViewDTO>(
        {
          name: groupColumnText.name,
          institution: (row) => groupColumnText.institution(row, meta),
          address: groupColumnText.address,
          manager: groupColumnText.manager,
          laboratories: (row) => groupColumnText.laboratories(row, meta),
        },
        (id) => t(`groups.columns.${id as GroupColumnId}`),
      ),
    [meta, t],
  );

  const institutionOptions = useMemo(
    () =>
      (institutionData?.items ?? [])
        .filter((institution) => institution.id && institution.name)
        .map((institution) => ({ value: institution.id ?? '', label: institution.name ?? '' })),
    [institutionData],
  );
  const overrides = useMemo<TableFilterFieldOverrides>(
    () => ({
      options: { [GROUPS_INSTITUTION_FIELD]: institutionOptions },
      extra: [
        { id: GROUPS_CITY_FIELD, label: t('groups.filters.city'), type: 'text', options: [] },
      ],
    }),
    [institutionOptions, t],
  );

  return <DataScreenFilter columns={columns} rows={data?.items ?? []} overrides={overrides} />;
}
